import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, 
  Calendar, 
  Building2, 
  Users, 
  Sparkles, 
  Wrench,
  Receipt,
  ChevronDown,
  HardHat,
  BarChart3,
  FileText,
  ClipboardList,
  RefreshCw,
  Shield,
  LogOut,
  TrendingUp,
  Hammer,
  CreditCard,
  Mail,
  MessageSquare,
  Eye,
  EyeOff,
  Folder,
  UserCog,
  Send,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { usePresentMode } from '@/contexts/PresentModeContext';
import { useChatNotifications } from '@/hooks/useChatNotifications';
import PlanQuotaIndicator from '@/components/PlanQuotaIndicator';

const ROLE_COLORS = {
  property_manager: 'bg-purple-500',
  owner: 'bg-blue-500',
  cleaner: 'bg-emerald-500',
  maintenance: 'bg-amber-500',
  landscaping: 'bg-green-500',
  repair: 'bg-red-500',
};

const ROLE_LABELS = {
  property_manager: 'Property Manager',
  owner: 'Owner',
  cleaner: 'Cleaner',
  maintenance: 'Maintenance',
  landscaping: 'Landscaping',
  repair: 'Repair',
};

export const Sidebar = ({ 
  activeView, 
  properties, 
  selectedProperty, 
  onSelectProperty,
  mobileOpen = false,
  onCloseMobile = () => {},
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, canManageUsers, isPlatformAdmin, isTruePlatformAdmin, viewAsCustomer, toggleViewAsCustomer } = useAuth();
  const { presentMode } = usePresentMode();
  const { totalUnread } = useChatNotifications();

  // In presentation mode, swap the demo manager's on-camera name for a
  // realistic-looking one so recordings don't say "Demo Manager".
  const displayName = presentMode && user?.email === 'admin@propveiw.com'
    ? 'Sarah Mitchell'
    : user?.name || 'User';
  const displayEmail = presentMode && user?.email === 'admin@propveiw.com'
    ? 'sarah@coastalstays.co'
    : user?.email;

  // Auto-close the mobile drawer after navigating to a new page.
  const go = (path) => {
    navigate(path);
    onCloseMobile();
  };

  const baseNavItems = [
    { id: 'home', label: 'Dashboard', icon: Home, path: '/dashboard' },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp, path: '/analytics' },
    { id: 'calendar', label: 'Calendar', icon: Calendar, path: '/calendar' },
    { id: 'calendar-sync', label: 'Calendar Sync', icon: RefreshCw, path: '/calendar-sync' },
    { id: 'properties', label: 'Properties', icon: Building2, path: '/properties' },
    { id: 'files', label: 'Files', icon: Folder, path: '/files' },
    { id: 'bookings', label: 'Bookings', icon: Users, path: '/bookings' },
    { id: 'team-chat', label: 'Team Chat', icon: MessageSquare, path: '/team-chat' },
    { id: 'cleaning', label: 'Cleaning', icon: Sparkles, path: '/cleaning' },
    { id: 'maintenance', label: 'Maintenance', icon: Wrench, path: '/maintenance' },
    { id: 'renovations', label: 'Renovations', icon: Hammer, path: '/renovations' },
    { id: 'expenses', label: 'Expenses', icon: Receipt, path: '/expenses' },
    { id: 'workers', label: 'Workers', icon: HardHat, path: '/workers' },
    { id: 'reports', label: 'Reports', icon: BarChart3, path: '/reports' },
    { id: 'invoices', label: 'Invoices', icon: FileText, path: '/invoices' },
    { id: 'work-orders', label: 'Work Orders', icon: ClipboardList, path: '/work-orders' },
    { id: 'billing', label: 'Billing', icon: CreditCard, path: '/billing' },
  ];

  // Users: visible to property managers (org-level user mgmt).
  // Waitlist: visible ONLY to platform admins (PROP VEIW staff).
  let navItems = [...baseNavItems];
  if (canManageUsers()) {
    navItems.push({ id: 'users', label: 'Users', icon: Shield, path: '/users' });
  }
  if (isPlatformAdmin) {
    navItems.push({ id: 'waitlist', label: 'Waitlist', icon: Mail, path: '/waitlist' });
    navItems.push({ id: 'email-health', label: 'Email Health', icon: Send, path: '/email-health' });
  }

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Mobile backdrop - covers everything to the right of the drawer */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={onCloseMobile}
          data-testid="sidebar-backdrop"
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed left-0 top-0 w-64 h-screen bg-slate-800 border-r border-slate-700 flex flex-col z-40 transition-transform duration-200 ease-out ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0`}
        data-testid="sidebar"
      >
      {/* Logo */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center gap-3">
          <img 
            src="/logo.png" 
            alt="PROP VEIW" 
            className="w-20 h-20 object-contain rounded-xl bg-white/95 p-1.5 ring-1 ring-amber-300/40 shadow-lg shadow-amber-500/10"
          />
          <div>
            <h1 className="font-manrope font-bold text-lg text-white tracking-tight">
              PROP VEIW
            </h1>
            <p className="text-xs text-slate-400 font-ibm-plex">
              Property Management
            </p>
          </div>
        </div>
      </div>

      {/* Property Selector */}
      <div className="p-4 border-b border-slate-700">
        <p className="text-xs uppercase tracking-wider text-slate-400 mb-2 font-ibm-plex font-medium">
          Active Property
        </p>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              className="w-full justify-between text-left font-normal bg-slate-700 border-slate-600 hover:bg-slate-600 text-white"
              data-testid="property-selector"
            >
              <span className="truncate">
                {selectedProperty?.name || 'All Properties'}
              </span>
              <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="start">
            <DropdownMenuItem
              onClick={() => onSelectProperty(null)}
              data-testid="property-option-all"
            >
              All Properties
            </DropdownMenuItem>
            {properties.map((property) => (
              <DropdownMenuItem
                key={property.id}
                onClick={() => onSelectProperty(property)}
                data-testid={`property-option-${property.id}`}
              >
                {property.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          
          return (
            <button
              key={item.id}
              onClick={() => go(item.path)}
              className={`
                w-full flex items-center gap-3 px-4 py-2.5 rounded-lg
                transition-colors-fast text-sm font-medium
                ${active 
                  ? 'bg-slate-600 text-white' 
                  : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }
              `}
              data-testid={`nav-${item.id}`}
            >
              <Icon className="h-5 w-5" />
              <span className="flex-1 text-left">{item.label}</span>
              {item.id === 'team-chat' && totalUnread > 0 && (
                <span
                  data-testid="sidebar-chat-unread"
                  className="text-[10px] font-bold rounded-full bg-red-500 text-white px-1.5 py-0.5 min-w-[1.25rem] text-center"
                >
                  {totalUnread > 99 ? '99+' : totalUnread}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Plan quota indicator (managers/owners only) */}
      <PlanQuotaIndicator />

      {/* Footer - User Info */}
      <div className="p-4 border-t border-slate-700">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center gap-3 hover:bg-slate-700 rounded-lg p-2 transition-colors">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${ROLE_COLORS[user?.role] || 'bg-slate-600'}`}>
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  displayName.charAt(0)?.toUpperCase() || 'U'
                )}
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-white truncate">{displayName}</p>
                <p className="text-xs text-slate-400">{ROLE_LABELS[user?.role] || user?.role}</p>
              </div>
              <ChevronDown className="h-4 w-4 text-slate-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{displayName}</p>
              <p className="text-xs text-slate-500">{displayEmail}</p>
              {isTruePlatformAdmin && !presentMode && (
                <p className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold mt-1">
                  Platform Admin
                </p>
              )}
            </div>
            {isTruePlatformAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={toggleViewAsCustomer}
                  className="cursor-pointer"
                  data-testid="toggle-view-as-customer"
                >
                  {viewAsCustomer ? (
                    <>
                      <EyeOff className="h-4 w-4 mr-2 text-amber-600" />
                      Exit customer view
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      View as customer
                    </>
                  )}
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => go('/account')}
              className="cursor-pointer"
              data-testid="nav-account"
            >
              <UserCog className="h-4 w-4 mr-2" />
              My Account
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => { logout(); onCloseMobile(); navigate('/login'); }} className="text-red-600">
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
    </>
  );
};

export default Sidebar;
